import { useEffect, useMemo, useState } from "react";
import AffiliateShell from "@/components/feature/AffiliateShell";
import MessageStudio from "./components/MessageStudio";
import {
  fetchMyReferralCode,
  fetchMyWebinarLeads,
  fetchMyWebinarStats,
  SOURCE_LABELS,
  suggestedWebinarMessage,
  TEMP_META,
  webinarLink,
  type MyWebinarLead,
  type MyWebinarStats,
} from "@/services/webinar";

const PURPLE = "#7C3AED";

const CHANNELS: { key: string; label: string; icon: string; cls: string }[] = [
  { key: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-fill", cls: "text-[#25D366]" },
  { key: "instagram", label: "Instagram", icon: "ri-instagram-line", cls: "text-[#E1306C]" },
  { key: "facebook", label: "Facebook", icon: "ri-facebook-circle-fill", cls: "text-[#1877F2]" },
  { key: "tiktok", label: "TikTok", icon: "ri-tiktok-fill", cls: "text-slate-900" },
  { key: "telegram", label: "Telegram", icon: "ri-telegram-fill", cls: "text-[#229ED9]" },
  { key: "link", label: "Link directo", icon: "ri-links-line", cls: "text-violet-600" },
];

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <section className={`bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>{children}</section>;
}

const INVITE_TEXT = "Te invito a ver este webinar de Vívelo AI sobre cómo desarrollar tu potencial con propósito. Es corto y gratuito 👇";

export default function WebinarAfiliado() {
  const [code, setCode] = useState<string | null>(null);
  const [stats, setStats] = useState<MyWebinarStats | null>(null);
  const [leads, setLeads] = useState<MyWebinarLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState("whatsapp");
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<"todos" | "viendo" | "terminaron" | "calientes">("todos");

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2200);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [c, s, l] = await Promise.all([fetchMyReferralCode(), fetchMyWebinarStats(30), fetchMyWebinarLeads()]);
        if (!active) return;
        setCode(c);
        setStats(s);
        setLeads(l);
      } catch {
        if (active) setError("No pudimos cargar tu información del webinar.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const link = code ? webinarLink(code, channel) : "";

  const copy = async (text: string, msg = "Link copiado") => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(msg);
    } catch {
      showToast("No se pudo copiar. Selecciona el texto y cópialo.");
    }
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`${INVITE_TEXT}\n${link}`)}`, "_blank", "noopener");

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Webinar Vívelo AI", text: INVITE_TEXT, url: link });
        return;
      } catch {
        return;
      }
    }
    void copy(link);
  };

  const shown = useMemo(() => {
    switch (filter) {
      case "viendo":
        return leads.filter((l) => l.maxPercent > 0 && !l.completed);
      case "terminaron":
        return leads.filter((l) => l.completed);
      case "calientes":
        return leads.filter((l) => l.temperature === "caliente" || l.temperature === "muy_caliente");
      default:
        return leads;
    }
  }, [leads, filter]);

  const sources = Object.entries(stats?.bySource ?? {}).sort((a, b) => b[1] - a[1]);
  const maxSource = Math.max(1, ...sources.map(([, n]) => n));

  const kpis = [
    { label: "Invitados", value: stats?.leads ?? 0, icon: "ri-user-add-line", tint: "bg-violet-50 text-violet-600" },
    { label: "Vieron el video", value: stats?.started ?? 0, icon: "ri-play-circle-line", tint: "bg-sky-50 text-sky-600" },
    { label: "Llegaron al 50 %", value: stats?.p50 ?? 0, icon: "ri-progress-5-line", tint: "bg-amber-50 text-amber-600" },
    { label: "Lo terminaron", value: stats?.completed ?? 0, icon: "ri-checkbox-circle-line", tint: "bg-emerald-50 text-emerald-600" },
  ];

  return (
    <AffiliateShell>
      <div className="max-w-5xl mx-auto flex flex-col gap-5">
        <header>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Webinar de Vívelo</h1>
          <p className="text-sm text-slate-600 mt-1">Comparte tu link único: sabrás quién entró por ti y desde qué canal. Los invitados que no terminen reciben recordatorios automáticos.</p>
        </header>

        {error ? <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div> : null}

        <div className="grid gap-5">
          <Card className="p-5">
            <h2 className="font-heading font-bold text-slate-900">Tu link único del webinar</h2>
            <p className="text-xs text-slate-500 mt-0.5">Elige el canal donde lo vas a compartir; el sistema lo registra en cada invitado.</p>
            <div className="mt-3 flex flex-wrap gap-2" role="tablist" aria-label="Canal">
              {CHANNELS.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={channel === c.key}
                  onClick={() => setChannel(c.key)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-semibold cursor-pointer whitespace-nowrap ${channel === c.key ? "border-violet-400 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                >
                  <i className={`${c.icon} ${c.cls} mr-1`}></i>
                  {c.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <i className="ri-links-line text-violet-600"></i>
              <input readOnly value={loading ? "Cargando…" : link} onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 bg-transparent text-sm text-slate-800 outline-none" aria-label="Tu link del webinar" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => copy(link)} disabled={!code} className="px-4 py-2.5 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50" style={{ background: PURPLE }}>
                <i className="ri-file-copy-line mr-1.5"></i>Copiar link
              </button>
              <button onClick={shareWhatsApp} disabled={!code} className="px-4 py-2.5 rounded-lg bg-[#25D366] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">
                <i className="ri-whatsapp-fill mr-1.5"></i>Enviar por WhatsApp
              </button>
              <button onClick={share} disabled={!code} className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50">
                <i className="ri-share-line mr-1.5"></i>Compartir
              </button>
            </div>
          </Card>

        </div>

        <MessageStudio code={code} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map((k) => (
            <Card key={k.label} className="p-4">
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.tint}`}>
                <i className={`${k.icon} text-lg`}></i>
              </span>
              <div className="mt-2 text-2xl font-extrabold text-slate-900 tabular-nums">{k.value}</div>
              <div className="text-xs text-slate-500">{k.label}</div>
            </Card>
          ))}
        </div>

        <Card className="p-5">
          <h2 className="font-heading font-bold text-slate-900">¿De dónde vienen tus invitados?</h2>
          {sources.length === 0 ? (
            <p className="text-sm text-slate-500 mt-3">Aún no hay invitados. Comparte tu link y aquí verás el canal de cada uno (últimos 30 días).</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-2.5">
              {sources.map(([s, n]) => (
                <li key={s} className="flex items-center gap-3">
                  <span className="w-28 text-xs font-semibold text-slate-700 truncate">{SOURCE_LABELS[s] ?? s}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(n / maxSource) * 100}%`, background: PURPLE }}></div>
                  </div>
                  <span className="w-8 text-right text-sm font-bold text-slate-900 tabular-nums">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-bold text-slate-900">Mis invitados</h2>
            <div className="flex flex-wrap gap-1.5">
              {([
                ["todos", "Todos"],
                ["viendo", "Viendo"],
                ["terminaron", "Terminaron"],
                ["calientes", "Calientes"],
              ] as const).map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap ${filter === k ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {shown.length === 0 ? (
            <p className="py-8 text-sm text-slate-500 text-center">{loading ? "Cargando…" : "No hay invitados en esta vista todavía."}</p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100">
              {shown.map((l) => {
                const t = TEMP_META[l.temperature] ?? TEMP_META.frio;
                return (
                  <li key={l.id} className="py-3 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[10rem]">
                      <div className="text-sm font-bold text-slate-900 truncate">{l.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {l.country ?? "—"} · {l.source ? SOURCE_LABELS[l.source] ?? l.source : "Sin canal"} · {new Date(l.createdAt).toLocaleDateString("es")}
                      </div>
                    </div>
                    <div className="w-32">
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${l.maxPercent}%` }}></div>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{l.completed ? "Lo terminó" : `${l.maxPercent}% visto`}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${t.cls}`}>{t.label}</span>
                    <div className="flex gap-1.5">
                      <a
                        href={`https://wa.me/${l.phone}?text=${encodeURIComponent(suggestedWebinarMessage(l))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-xs font-bold cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-fill mr-1"></i>Escribirle
                      </a>
                      <button onClick={() => copy(`${window.location.origin}/webinar?t=${l.token}`, "Link de retomar copiado")} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs cursor-pointer" aria-label="Copiar link para retomar el webinar">
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[11px] text-slate-400 mt-4">Escríbeles de forma personal y respetuosa; quienes respondan STOP dejan de recibir recordatorios automáticos.</p>
        </Card>
      </div>

      {toast ? (
        <div role="status" className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-[#0B1F3A] text-white text-sm shadow-lg">
          {toast}
        </div>
      ) : null}
    </AffiliateShell>
  );
}
